import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ClipboardList, UserCheck, Pencil, Check, X, Trash2, Plus, ExternalLink } from "lucide-react";

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toFixed(2)}`);
const VSC_OPTS = [["new_car", "New Car"], ["preowned", "Pre-Owned"], ["blended", "Blended"]];

function AssignSupport({ onboardingId, current, onSaved }) {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/onboarding/support-candidates").then(({ data }) => setUsers(data || [])).catch(() => {}); }, []);
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${onboardingId}/assign-support`, { user_id: uid || null, note });
      toast.success(data.assigned_to ? `Assigned to ${data.assigned_to}${data.emailed ? " · emailed" : " (email failed)"}` : "Assignment cleared");
      setNote(""); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_auto] gap-2 items-end" data-testid="assign-support-block">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Assign set-up to</div>
        <select value={uid} onChange={(e) => setUid(e.target.value)} className="h-10 w-full rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="assign-support-select">
          <option value="">{current ? `Current: ${current} — pick to change` : "Select support user…"}</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mb-1">Note to assignee (optional)</div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Dealer wants to go live Friday" className="h-10 rounded-sm" data-testid="assign-support-note" />
      </div>
      <Button onClick={assign} disabled={busy || !uid} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold" data-testid="assign-support-btn">
        <UserCheck size={14} className="mr-1.5" /> Assign
      </Button>
    </div>
  );
}

function ProgramRow({ onboardingId, row, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ dealer_over_remit: row.dealer_over_remit ?? "", agent_commission: row.agent_commission ?? "", admin_fee: row.admin_fee ?? "", notes: row.notes || "", vsc_programs: row.vsc_programs || [] });
  const save = async () => {
    try {
      await api.put(`/onboarding/${onboardingId}/programs/${row.line_id}`, {
        dealer_over_remit: f.dealer_over_remit === "" ? null : Number(f.dealer_over_remit),
        agent_commission: f.agent_commission === "" ? null : Number(f.agent_commission),
        admin_fee: f.admin_fee === "" ? null : Number(f.admin_fee),
        clear_admin_fee: f.admin_fee === "" && row.admin_fee != null,
        notes: f.notes,
        vsc_programs: f.vsc_programs,
      });
      toast.success(`${row.product_name} updated`); setEdit(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${row.product_name} from this install?`)) return;
    try { await api.delete(`/onboarding/${onboardingId}/programs/${row.line_id}`); toast.success("Program removed"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  return (
    <tr className="border-b border-[#F1F3F5]" data-testid={`install-preview-row-${row.line_id}`}>
      <td className="py-2 pr-3"><div className="font-bold text-sm text-[#0B132B]">{row.product_name}</div><div className="text-[10px] uppercase tracking-[0.14em] text-[#6C757D]">{row.vertical_key}</div>
        {row.vertical_key === "automotive" && /vsc/i.test(row.product_name || "") && !/subscription/i.test(row.product_name || "") && (
          <div className="mt-1 flex flex-wrap gap-1" data-testid={`vsc-programs-${row.line_id}`}>
            {VSC_OPTS.map(([k, l]) => {
              const on = (row.vsc_programs || []).includes(k);
              return (
                <button key={k} type="button" disabled={!edit} onClick={() => setF({ ...f, vsc_programs: on ? (f.vsc_programs || []).filter((x) => x !== k) : [...(f.vsc_programs || []), k] })}
                  className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold ${(edit ? (f.vsc_programs || []).includes(k) : on) ? "bg-[#0B132B] border-[#0B132B] text-white" : "bg-white border-[#E9ECEF] text-[#6C757D]"}`}
                  data-testid={`vsc-program-${row.line_id}-${k}`}>{l}</button>
              );
            })}
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-sm">{edit ? <Input type="number" step="0.01" value={f.dealer_over_remit} onChange={(e) => setF({ ...f, dealer_over_remit: e.target.value })} className="h-8 w-28 rounded-sm" data-testid={`edit-remit-${row.line_id}`} /> : <span data-testid={`remit-${row.line_id}`}>{money(row.dealer_over_remit)}</span>}</td>
      <td className="py-2 pr-3 text-sm">{edit ? <Input type="number" step="0.01" value={f.agent_commission} onChange={(e) => setF({ ...f, agent_commission: e.target.value })} className="h-8 w-28 rounded-sm" data-testid={`edit-commission-${row.line_id}`} /> : <span data-testid={`commission-${row.line_id}`}>{money(row.agent_commission)}</span>}</td>
      <td className="py-2 pr-3 text-sm">{edit ? <Input type="number" step="0.01" min="0" value={f.admin_fee} placeholder={`std ${money(row.standard_admin_fee)}`} onChange={(e) => setF({ ...f, admin_fee: e.target.value })} className="h-8 w-28 rounded-sm border-[#D4AF37]/60" data-testid={`edit-admin-fee-${row.line_id}`} /> : <span data-testid={`admin-fee-${row.line_id}`} className={row.admin_fee != null ? "font-bold text-[#0B132B]" : "text-[#6C757D]"}>{money(row.effective_admin_fee)}{row.admin_fee != null ? <span className="ml-1 text-[9px] uppercase tracking-wider text-[#D4AF37] font-bold">override</span> : <span className="ml-1 text-[9px] uppercase tracking-wider">std</span>}
        {row.admin_fee == null && (row.standard_fee_breakdown || []).length > 0 && <div className="text-[10px] text-[#6C757D] mt-0.5" data-testid={`admin-fee-breakdown-${row.line_id}`}>{row.standard_fee_breakdown.map((b) => `${b.label} ${money(b.fee)}`).join(" · ")}</div>}</span>}</td>
      <td className="py-2 pr-3 text-xs text-[#6C757D]">{edit ? <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="h-8 rounded-sm" /> : (row.notes || "—")}</td>
      <td className="py-2 text-xs"><span className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-[0.12em] font-bold ${row.install_status === "installed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-[#F8F9FA] text-[#6C757D] border-[#E9ECEF]"}`}>{(row.install_status || "not_started").replace("_", " ")}</span></td>
      <td className="py-2 text-right whitespace-nowrap">
        {edit ? (
          <>
            <Button size="sm" onClick={save} className="h-8 rounded-sm bg-[#0B132B] text-white mr-1" data-testid={`save-program-${row.line_id}`}><Check size={12} /></Button>
            <Button size="sm" variant="outline" onClick={() => setEdit(false)} className="h-8 rounded-sm"><X size={12} /></Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => setEdit(true)} className="h-8 rounded-sm border-[#0B132B]/20 mr-1" data-testid={`edit-program-${row.line_id}`}><Pencil size={12} /></Button>
            <Button size="sm" variant="outline" onClick={remove} className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid={`remove-program-${row.line_id}`}><Trash2 size={12} /></Button>
          </>
        )}
      </td>
    </tr>
  );
}

function AddProgram({ onboardingId, onSaved }) {
  const [cat, setCat] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_id: "", vertical_key: "", dealer_over_remit: "", agent_commission: "" });
  useEffect(() => { if (open && !cat) api.get("/onboarding/catalog").then(({ data }) => setCat(data)).catch(() => {}); }, [open, cat]);
  const add = async () => {
    try {
      await api.post(`/onboarding/${onboardingId}/programs`, {
        product_id: f.product_id, vertical_key: f.vertical_key,
        dealer_over_remit: f.dealer_over_remit === "" ? null : Number(f.dealer_over_remit),
        agent_commission: f.agent_commission === "" ? null : Number(f.agent_commission),
      });
      toast.success("Program added"); setOpen(false); setF({ product_id: "", vertical_key: "", dealer_over_remit: "", agent_commission: "" }); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  if (!open) return <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="h-9 rounded-sm border-[#0B132B]/20" data-testid="add-program-btn"><Plus size={13} className="mr-1" /> Add program</Button>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_0.8fr_0.8fr_auto] gap-2 items-end p-3 border border-dashed border-[#D4AF37] rounded-sm bg-[#D4AF37]/5" data-testid="add-program-form">
      <select value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} className="h-9 rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="add-program-product">
        <option value="">Product…</option>{(cat?.products || []).filter((p) => p.active !== false).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select value={f.vertical_key} onChange={(e) => setF({ ...f, vertical_key: e.target.value })} className="h-9 rounded-sm border border-[#0B132B]/15 bg-white px-2 text-sm" data-testid="add-program-vertical">
        <option value="">Vertical…</option>{(cat?.verticals || []).map((v) => <option key={v.id} value={v.key}>{v.name}</option>)}
      </select>
      <Input type="number" step="0.01" placeholder="Over remit $" value={f.dealer_over_remit} onChange={(e) => setF({ ...f, dealer_over_remit: e.target.value })} className="h-9 rounded-sm" />
      <Input type="number" step="0.01" placeholder="Agent comm $" value={f.agent_commission} onChange={(e) => setF({ ...f, agent_commission: e.target.value })} className="h-9 rounded-sm" />
      <div className="flex gap-1">
        <Button size="sm" onClick={add} disabled={!f.product_id || !f.vertical_key} className="h-9 rounded-sm bg-[#0B132B] text-white" data-testid="add-program-save"><Check size={12} /></Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-9 rounded-sm"><X size={12} /></Button>
      </div>
    </div>
  );
}

export default function InstallPreviewPanel({ onboardingId, onChanged }) {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get(`/onboarding/${onboardingId}/install-preview`); setData(data); } catch (e) { /* non-admin */ } };
  useEffect(() => { load(); }, [onboardingId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { load(); onChanged?.(); };
  if (!data) return null;
  return (
    <Card className="p-5 border-[#D4AF37]/40 rounded-sm mb-4 bg-white" data-testid="install-preview-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37] flex items-center gap-1"><ClipboardList size={12} /> Install preview & commissions</div>
          <div className="text-sm text-[#6C757D] mt-1">Super-admin view of everything production will install. Edit values here if something was entered incorrectly — every change is logged.</div>
        </div>
        <div className="text-right text-xs text-[#6C757D]">
          <div>Production owner: <b className="text-[#0B132B]" data-testid="install-preview-owner">{data.assigned_production_user_name || "unassigned"}</b></div>
          {data.subagent_commission_markup != null && <div>Subagent markup: <b className="text-[#0B132B]">{money(data.subagent_commission_markup)}</b></div>}
          {data.agency_commission != null && <div>Agency commission: <b className="text-[#0B132B]">{money(data.agency_commission)}</b></div>}
        </div>
      </div>

      <div className="mb-4 pb-4 border-b border-[#F1F3F5]">
        <AssignSupport onboardingId={onboardingId} current={data.assigned_production_user_name} onSaved={refresh} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] border-b border-[#E9ECEF]">
            <th className="py-2 pr-3">Program</th><th className="py-2 pr-3">Dealer over-remit</th><th className="py-2 pr-3">Agent commission</th><th className="py-2 pr-3 text-[#D4AF37]">Admin fee (AWG only)</th><th className="py-2 pr-3">Notes</th><th className="py-2">Install</th><th />
          </tr></thead>
          <tbody>{data.products.map((r) => <ProgramRow key={r.line_id} onboardingId={onboardingId} row={r} onSaved={refresh} />)}</tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <AddProgram onboardingId={onboardingId} onSaved={refresh} />
        {data.subscription_landing_url && (
          <a href={data.subscription_landing_url} target="_blank" rel="noreferrer" className="text-xs text-[#0B132B] underline inline-flex items-center gap-1" data-testid="install-preview-sub-link"><ExternalLink size={12} /> Subscription landing page</a>
        )}
      </div>
      {data.program_edit_log?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#F1F3F5]" data-testid="program-edit-log">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-1">Correction log</div>
          <ul className="space-y-0.5 text-[11px] text-[#6C757D]">
            {[...data.program_edit_log].reverse().map((e, i) => (
              <li key={i}><span className="text-[#0B132B] font-semibold">{e.by}</span> {e.action} {e.product_name}{e.changes ? ` — ${Object.entries(e.changes).map(([k, c]) => `${k}: ${c.from ?? "—"} → ${c.to}`).join(", ")}` : ""} · {new Date(e.at).toLocaleString()}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
